import { useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"

import ModalService from "src/modals"
import { playUiSound } from "src/utils/audio"
import { useIsPhone } from "src/utils/viewport"
import "./Modal.css"

/* `width`, `maxHeight`, `top` and `bottom` are written as **inline styles**, which is what every
   caller wants on a desktop — each modal states its own size — and which quietly beat every
   stylesheet on a phone. The full-bleed rules in Modal.css were correct and never once applied:
   the sign-up form was rendering at `width: 50%`, 195px on a 390px screen, with its placeholders
   clipped mid-word and "Already have an account?" wrapped over three lines. An inline style has no
   media query and no `[data-shell]`, so the only place this can be decided is here.

   So on a phone the component emits no sizing at all and lets the stylesheet own it. Nothing
   changes anywhere else — the desktop path is the same object it always built.

   The **dismiss button** is the other half of that, and it is a door rather than a decoration. A
   modal is normally left by clicking the backdrop or pressing Escape; making the sheet full-bleed
   covers every pixel of the backdrop, and a phone has no Escape. So on a phone a modal without a
   close control of its own is a room with no exit — which is most of them: the research sheet, a
   a unit's stats, the login and sign-up forms, every battle screen. Verified on the
   research sheet at 390px, where the wrapper and the sheet measure the same box and there is no
   backdrop element at all.

   One button here rather than a close control added to twenty-five modals, and it is also the
   better answer for the ones that *do* have one: theirs is usually a Cancel at the bottom of a
   form, which on a 2000px scroll is not a door anybody finds. On a phone every sheet is now left
   the same way, from the same corner. */
/* Everything a keyboard can land on, in the order Tab visits it. `disabled` and `tabindex="-1"`
   are out; so is anything with no box, which is how a collapsed panel's controls stay out of the
   cycle. */
const FOCUSABLE =
  "a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])"

const Modal = ({ width = "50%", isolated = true, scope = "screen", top, bottom, maxHeight = "90vh", className = "", children }) => {
  const isPhone = useIsPhone()
  const { t } = useTranslation()
  const dialog = useRef(null)
  const handleClose = (e) => {
    if (e.target.className === "background") {
      playUiSound("close")
      ModalService.hide()
    }
  }

  /* A dialog has to hold the keyboard. Without this, Tab walked straight out of the shop and onto
     CREATE GAME, JOIN and the account chip on the page underneath — measured at 15 of 26 presses —
     so somebody navigating by keyboard ends up operating a screen they cannot see, under a sheet
     they never left. Escape already closes (see modals.js); this is the other half.

     Focus goes to the dialog *box*, never to the first field: landing in an input opens the
     on-screen keyboard on a phone, over a sheet the player has not read yet. And whatever had
     focus before gets it back on close, which is what makes a modal opened over another hand the
     keyboard back to the one beneath rather than to the top of the page. */
  useEffect(() => {
    const box = dialog.current
    if (!box) return undefined
    const previous = document.activeElement
    box.focus({ preventScroll: true })
    return () => {
      if (previous && typeof previous.focus === "function" && document.contains(previous)) {
        previous.focus({ preventScroll: true })
      }
    }
  }, [])

  const keepFocusInside = (e) => {
    if (e.key !== "Tab") return
    const box = dialog.current
    if (!box) return
    const stops = [...box.querySelectorAll(FOCUSABLE)].filter((el) => el.getBoundingClientRect().width > 0)
    if (stops.length === 0) {
      // Nothing to move to; keeping the box itself focused beats letting Tab leave.
      e.preventDefault()
      return
    }
    const first = stops[0]
    const last = stops[stops.length - 1]
    // `document.activeElement` is the box itself on the first press, which belongs to neither end —
    // hence the explicit wrap in both directions rather than a check against `first` alone.
    if (e.shiftKey && (document.activeElement === first || document.activeElement === box)) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault()
      first.focus()
    }
  }

  let style = {}
  if (isPhone) {
    // The sheet fills the screen; a top offset would only push it off the bottom.
  } else if (top) {
    // Scoped modals keep the wrapper's own top (the map's top edge); pinning top: 0 here put the
    // sheet over the command bar.
    style = { ...style, ...(scope === "map" ? {} : { top: 0 }), paddingTop: top, alignItems: "flex-start" }
  } else if (bottom) {
    style = { ...style, bottom: 0, paddingBottom: bottom, alignItems: "flex-end" }
  }

  return (
    <div
      className={`modal${scope === "map" ? " modal-scope-map" : ""} ${!isolated ? "dont-capture-events" : ""}${className ? ` ${className}` : ""}`}
      onClick={handleClose}
      style={style}
    >
      {isolated ? <div className="background" /> : null}
      <div
        ref={dialog}
        className="modal-content with-scroll-bar vertical"
        style={isPhone ? undefined : { width, maxHeight }}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        onKeyDown={keepFocusInside}
      >
        {/* Everywhere, not phone-only any more (owner's Q22): backdrop clicks and Escape are
            real doors, but doors a new player has to know about — an explicit ✕ is the one
            everybody finds. On a desktop it sits in the sheet's own corner and stays put while
            the sheet scrolls; the phone keeps its fixed corner button below. */}
        {!isPhone && (
          <button
            type="button"
            className="modal-dismiss modal-dismiss-desktop"
            aria-label={t("common.close")}
            onClick={() => {
              playUiSound("close")
              ModalService.hide()
            }}
          >
            &#215;
          </button>
        )}
        {children}
      </div>
      {isPhone ? (
        <button
          type="button"
          className="modal-dismiss"
          aria-label={t("common.close")}
          onClick={() => {
            playUiSound("close")
            ModalService.hide()
          }}
        >
          &#215;
        </button>
      ) : null}
    </div>
  )
}

export default Modal
